import React, { useState, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  BookOpen, Sparkles, Loader2, Printer, Trash2, ChevronDown, ChevronUp,
  Clock, Target, Languages, Lightbulb, MessageSquare, Home, CheckCircle, FileText, Download
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CONTENT_AREAS = [
  "ELA / Language Arts",
  "Mathematics",
  "Science",
  "Social Studies",
  "The Arts",
  "Health / Physical Education",
  "Technology",
  "General / Cross-Curricular",
];

const GRADE_BANDS = [
  { value: "K", label: "Kindergarten" },
  { value: "1", label: "Grade 1" },
  { value: "2", label: "Grade 2" },
  { value: "3-4", label: "Grades 3-4" },
  { value: "5-6", label: "Grades 5-6" },
  { value: "7-8", label: "Grades 7-8" },
  { value: "9-12", label: "Grades 9-12" },
];

const WIDA_LEVELS = [
  { value: 1, label: "L1 Entering", color: "bg-red-500" },
  { value: 2, label: "L2 Emerging", color: "bg-orange-500" },
  { value: 3, label: "L3 Developing", color: "bg-yellow-500" },
  { value: 4, label: "L4 Expanding", color: "bg-blue-500" },
  { value: 5, label: "L5 Bridging", color: "bg-indigo-500" },
  { value: 6, label: "L6 Reaching", color: "bg-green-500" },
];

const PHASE_ICONS = {
  "Opening / Warm-Up": "bg-amber-100 text-amber-700",
  "Introduction / Modeling": "bg-blue-100 text-blue-700",
  "Guided Practice": "bg-green-100 text-green-700",
  "Independent Practice": "bg-purple-100 text-purple-700",
  "Closing / Assessment": "bg-red-100 text-red-700",
};

const LessonPlanTab = () => {
  const [view, setView] = useState('list'); // 'list' | 'create' | 'detail'
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [expandedPhase, setExpandedPhase] = useState(null);

  // Form state
  const [form, setForm] = useState({
    grade_band: '',
    content_area: '',
    wida_levels: [3, 4],
    topic: '',
    duration: '45 minutes',
    additional_notes: '',
  });

  const printRef = useRef(null);

  useEffect(() => { fetchPlans(); }, []);

  const fetchPlans = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/lesson-plans`);
      setPlans(res.data.plans || []);
    } catch (err) {
      console.error('Failed to fetch lesson plans:', err);
    } finally {
      setLoading(false);
    }
  };

  const toggleLevel = (lvl) => {
    setForm(prev => {
      const levels = prev.wida_levels.includes(lvl)
        ? prev.wida_levels.filter(l => l !== lvl)
        : [...prev.wida_levels, lvl].sort();
      return { ...prev, wida_levels: levels.length > 0 ? levels : [lvl] };
    });
  };

  const handleGenerate = async () => {
    // Read form values directly from DOM as fallback
    const gradeEl = document.querySelector('[data-testid="lp-grade-band"]');
    const contentEl = document.querySelector('[data-testid="lp-content-area"]');
    const topicEl = document.querySelector('[data-testid="lp-topic"]');

    const gradeVal = gradeEl?.value || form.grade_band;
    const contentVal = contentEl?.value || form.content_area;
    const topicVal = topicEl?.value || form.topic;

    // Sync state if DOM has values but state doesn't
    const payload = {
      ...form,
      grade_band: gradeVal,
      content_area: contentVal,
      topic: topicVal,
    };

    if (!payload.grade_band || !payload.content_area || !payload.topic.trim()) {
      toast.error('Please fill in grade band, content area, and topic');
      return;
    }
    setGenerating(true);
    try {
      const res = await axios.post(`${API}/lesson-plans/generate`, payload);
      toast.success('Lesson plan generated!');
      setSelectedPlan({
        plan_id: res.data.plan_id,
        lesson_plan: res.data.lesson_plan,
        ...res.data.metadata,
        created_at: new Date().toISOString(),
      });
      setView('detail');
      fetchPlans();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to generate lesson plan');
    } finally {
      setGenerating(false);
    }
  };

  const handleDelete = async (planId) => {
    if (!window.confirm('Delete this lesson plan?')) return;
    try {
      await axios.delete(`${API}/lesson-plans/${planId}`);
      toast.success('Lesson plan deleted');
      setPlans(prev => prev.filter(p => p.plan_id !== planId));
      if (selectedPlan?.plan_id === planId) {
        setSelectedPlan(null);
        setView('list');
      }
    } catch (err) {
      toast.error('Failed to delete');
    }
  };

  const handleDownloadPdf = async (planId) => {
    toast.info('Generating PDF...');
    try {
      const res = await axios.get(`${API}/lesson-plans/${planId}/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      const title = (selectedPlan?.lesson_plan?.title || selectedPlan?.topic || 'Lesson_Plan').replace(/[^a-zA-Z0-9 _-]/g, '_').substring(0, 60);
      link.download = `MOSAIC_Lesson_Plan_${title}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('PDF downloaded!');
    } catch (err) {
      toast.error('Failed to generate PDF');
    }
  };

  const handlePrint = () => {
    const printContent = printRef.current;
    if (!printContent) return;
    const win = window.open('', '_blank');
    win.document.write(`
      <html><head><title>MOSAIC Lesson Plan</title>
      <style>
        body { font-family: Georgia, serif; max-width: 800px; margin: 40px auto; padding: 0 20px; color: #1a1a1a; font-size: 11pt; }
        h1 { font-size: 18pt; margin-bottom: 4px; color: #002FA7; }
        h2 { font-size: 13pt; border-bottom: 2px solid #002FA7; padding-bottom: 4px; margin-top: 24px; color: #002FA7; }
        h3 { font-size: 11pt; margin-top: 16px; }
        .meta { font-size: 9pt; color: #666; margin-bottom: 20px; }
        .obj-box { background: #f0f4ff; border-left: 4px solid #002FA7; padding: 10px 14px; margin: 10px 0; }
        .phase { margin: 12px 0; padding: 10px; border: 1px solid #ddd; border-radius: 4px; }
        .phase-header { font-weight: bold; margin-bottom: 6px; }
        .frames { background: #fffbeb; border: 1px solid #f59e0b; padding: 8px 12px; margin: 6px 0; border-radius: 4px; }
        ul, ol { padding-left: 20px; }
        li { margin: 3px 0; }
        .footer { margin-top: 30px; border-top: 1px solid #ccc; padding-top: 10px; font-size: 8pt; color: #999; text-align: center; }
        @media print { body { margin: 20px; } }
      </style></head><body>
    `);
    win.document.write(printContent.innerHTML);
    win.document.write(`
      <div class="footer">Generated by MOSAIC Assessment Platform | WIDA is a registered trademark of the Board of Regents of the University of Wisconsin System.</div>
      </body></html>
    `);
    win.document.close();
    win.print();
  };

  const openPlan = (plan) => {
    setSelectedPlan(plan);
    setView('detail');
  };

  const formatDate = (iso) => {
    if (!iso) return '';
    return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
  };

  // ─── LIST VIEW ───
  if (view === 'list') {
    return (
      <div className="space-y-5" data-testid="lesson-plan-list">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-bold text-gray-800" style={{ fontFamily: 'Outfit, sans-serif' }}>WIDA Lesson Plans</h2>
            <p className="text-xs text-gray-500">MOSAIC-generated language-development lesson plans for your classroom</p>
          </div>
          <Button onClick={() => setView('create')} className="bg-[#002FA7] hover:bg-[#002FA7]/90 text-white gap-1" data-testid="create-lesson-plan-btn">
            <Sparkles size={14} /> Generate New Plan
          </Button>
        </div>

        {loading ? (
          <div className="py-12 text-center"><Loader2 className="animate-spin mx-auto text-[#002FA7]" size={24} /></div>
        ) : plans.length === 0 ? (
          <div className="bg-white border border-gray-200 rounded-lg p-10 text-center">
            <BookOpen size={40} className="mx-auto text-gray-300 mb-3" />
            <p className="text-gray-500 font-medium">No lesson plans yet</p>
            <p className="text-xs text-gray-400 mt-1 mb-4">Generate your first lesson plan in seconds</p>
            <Button onClick={() => setView('create')} variant="outline" className="gap-1" data-testid="empty-create-btn">
              <Sparkles size={14} /> Create First Plan
            </Button>
          </div>
        ) : (
          <div className="grid gap-3">
            {plans.map(plan => {
              const lp = plan.lesson_plan || {};
              return (
                <div key={plan.plan_id} className="bg-white border border-gray-200 rounded-lg p-4 hover:border-blue-300 hover:shadow-sm transition-all cursor-pointer group" onClick={() => openPlan(plan)} data-testid={`plan-card-${plan.plan_id}`}>
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-gray-800 text-sm truncate">{lp.title || plan.topic || 'Untitled Plan'}</h3>
                      <div className="flex items-center gap-2 mt-1 flex-wrap">
                        <span className="text-[10px] font-bold bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded">{plan.grade_band}</span>
                        <span className="text-[10px] font-bold bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded">{plan.content_area}</span>
                        {(plan.wida_levels || []).map(l => (
                          <span key={l} className={`text-[10px] font-bold text-white px-1.5 py-0.5 rounded ${WIDA_LEVELS.find(w => w.value === l)?.color || 'bg-gray-400'}`}>L{l}</span>
                        ))}
                      </div>
                      <p className="text-[10px] text-gray-400 mt-1">{formatDate(plan.created_at)}</p>
                    </div>
                    <button onClick={(e) => { e.stopPropagation(); handleDelete(plan.plan_id); }} className="p-1.5 text-gray-300 hover:text-red-500 hover:bg-red-50 rounded opacity-0 group-hover:opacity-100 transition-opacity" data-testid={`delete-plan-${plan.plan_id}`}>
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  // ─── CREATE VIEW ───
  if (view === 'create') {
    return (
      <div className="space-y-5" data-testid="lesson-plan-create">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-bold text-gray-800" style={{ fontFamily: 'Outfit, sans-serif' }}>Generate Lesson Plan</h2>
            <p className="text-xs text-gray-500">the automated system creates a complete language-development lesson plan from your inputs</p>
          </div>
          <Button onClick={() => setView('list')} variant="outline" size="sm">Back to Plans</Button>
        </div>

        <div className="bg-white border border-gray-200 rounded-lg p-5 space-y-5">
          {/* Grade & Content Area */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-bold text-gray-600 uppercase block mb-1.5">Grade Band *</label>
              <select value={form.grade_band} onChange={e => { const v = e.target.value; setForm(p => ({ ...p, grade_band: v })); }} onBlur={e => { const v = e.target.value; setForm(p => ({ ...p, grade_band: v })); }} className="w-full h-10 text-sm border border-gray-300 rounded-md px-3" data-testid="lp-grade-band">
                <option value="">Select grade band...</option>
                {GRADE_BANDS.map(g => <option key={g.value} value={g.value}>{g.label}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-bold text-gray-600 uppercase block mb-1.5">Content Area *</label>
              <select value={form.content_area} onChange={e => { const v = e.target.value; setForm(p => ({ ...p, content_area: v })); }} onBlur={e => { const v = e.target.value; setForm(p => ({ ...p, content_area: v })); }} className="w-full h-10 text-sm border border-gray-300 rounded-md px-3" data-testid="lp-content-area">
                <option value="">Select content area...</option>
                {CONTENT_AREAS.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
          </div>

          {/* Topic */}
          <div>
            <label className="text-xs font-bold text-gray-600 uppercase block mb-1.5">Topic / Theme *</label>
            <Input value={form.topic} onChange={e => setForm(p => ({ ...p, topic: e.target.value }))} placeholder="e.g., Comparing fractions using food from different cultures" className="h-10 border-gray-300" data-testid="lp-topic" />
          </div>

          {/* WIDA Levels */}
          <div>
            <label className="text-xs font-bold text-gray-600 uppercase block mb-1.5">Target Proficiency Levels</label>
            <div className="flex flex-wrap gap-2">
              {WIDA_LEVELS.map(lvl => {
                const isSelected = form.wida_levels.includes(lvl.value);
                return (
                  <button key={lvl.value} onClick={() => toggleLevel(lvl.value)} className={`px-3 py-1.5 text-xs font-bold rounded-full border-2 transition-all ${isSelected ? `${lvl.color} text-white border-transparent` : 'bg-white text-gray-500 border-gray-200 hover:border-gray-400'}`} data-testid={`lp-level-${lvl.value}`}>
                    {lvl.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Duration */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-bold text-gray-600 uppercase block mb-1.5">Lesson Duration</label>
              <select value={form.duration} onChange={e => { const v = e.target.value; setForm(p => ({ ...p, duration: v })); }} className="w-full h-10 text-sm border border-gray-300 rounded-md px-3" data-testid="lp-duration">
                <option value="30 minutes">30 minutes</option>
                <option value="45 minutes">45 minutes</option>
                <option value="60 minutes">60 minutes</option>
                <option value="90 minutes">90 minutes (block)</option>
              </select>
            </div>
          </div>

          {/* Additional Notes */}
          <div>
            <label className="text-xs font-bold text-gray-600 uppercase block mb-1.5">Additional Notes (optional)</label>
            <Textarea value={form.additional_notes} onChange={e => setForm(p => ({ ...p, additional_notes: e.target.value }))} placeholder="e.g., Students have limited prior knowledge of fractions. Include hands-on manipulatives." className="min-h-[70px] border-gray-300 text-sm" data-testid="lp-notes" />
          </div>

          {/* Generate Button */}
          <Button onClick={handleGenerate} disabled={generating} className="w-full h-12 bg-[#002FA7] hover:bg-[#002FA7]/90 text-white text-base font-bold" data-testid="lp-generate-btn">
            {generating ? (
              <><Loader2 size={18} className="animate-spin mr-2" /> Generating WIDA Lesson Plan...</>
            ) : (
              <><Sparkles size={18} className="mr-2" /> Generate Lesson Plan</>
            )}
          </Button>
        </div>
      </div>
    );
  }

  // ─── DETAIL VIEW ───
  if (view === 'detail' && selectedPlan) {
    const lp = selectedPlan.lesson_plan || {};
    return (
      <div className="space-y-4" data-testid="lesson-plan-detail">
        <div className="flex items-center justify-between">
          <Button onClick={() => setView('list')} variant="outline" size="sm">Back to Plans</Button>
          <div className="flex gap-2">
            <Button onClick={() => handleDownloadPdf(selectedPlan.plan_id)} variant="outline" size="sm" className="gap-1 border-[#002FA7] text-[#002FA7] hover:bg-blue-50" data-testid="lp-download-pdf-btn">
              <Download size={14} /> Download PDF
            </Button>
            <Button onClick={handlePrint} variant="outline" size="sm" className="gap-1" data-testid="lp-print-btn">
              <Printer size={14} /> Print
            </Button>
            <Button onClick={() => { setForm({ grade_band: selectedPlan.grade_band || '', content_area: selectedPlan.content_area || '', wida_levels: selectedPlan.wida_levels || [3,4], topic: selectedPlan.topic || '', duration: selectedPlan.duration || '45 minutes', additional_notes: '' }); setView('create'); }} variant="outline" size="sm" className="gap-1">
              <Sparkles size={14} /> Regenerate
            </Button>
          </div>
        </div>

        {/* Printable content */}
        <div ref={printRef}>
          {/* Header */}
          <div className="bg-white border border-gray-200 rounded-lg p-5">
            <h1 className="text-xl font-bold text-[#002FA7]" style={{ fontFamily: 'Outfit, sans-serif' }}>{lp.title || selectedPlan.topic}</h1>
            <div className="flex items-center gap-2 mt-2 flex-wrap">
              <span className="text-xs font-bold bg-blue-100 text-blue-700 px-2 py-0.5 rounded">{selectedPlan.grade_band}</span>
              <span className="text-xs font-bold bg-gray-100 text-gray-600 px-2 py-0.5 rounded">{selectedPlan.content_area}</span>
              <span className="text-xs font-bold bg-gray-100 text-gray-600 px-2 py-0.5 rounded flex items-center gap-1"><Clock size={10} /> {selectedPlan.duration}</span>
              {(selectedPlan.wida_levels || []).map(l => (
                <span key={l} className={`text-xs font-bold text-white px-2 py-0.5 rounded ${WIDA_LEVELS.find(w => w.value === l)?.color || 'bg-gray-400'}`}>L{l}</span>
              ))}
            </div>
          </div>

          {/* Objectives */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <div className="flex items-center gap-1.5 mb-1"><Target size={14} className="text-blue-700" /><span className="text-xs font-bold text-blue-700 uppercase">Content Objective</span></div>
                <p className="text-sm text-gray-800">{lp.content_objective}</p>
              </div>
              <div>
                <div className="flex items-center gap-1.5 mb-1"><Languages size={14} className="text-blue-700" /><span className="text-xs font-bold text-blue-700 uppercase">Language Objective</span></div>
                <p className="text-sm text-gray-800">{lp.language_objective}</p>
              </div>
            </div>
          </div>

          {/* WIDA Standards & Key Language Uses */}
          {(lp.wida_standards || lp.key_language_uses) && (
            <div className="bg-white border border-gray-200 rounded-lg p-4">
              <h2 className="text-sm font-bold text-gray-700 mb-2">WIDA Standards & Key Language Uses</h2>
              <div className="flex flex-wrap gap-2">
                {(lp.wida_standards || []).map((s, i) => (
                  <span key={i} className="text-xs bg-indigo-50 text-indigo-700 px-2 py-1 rounded border border-indigo-200">{s}</span>
                ))}
                {(lp.key_language_uses || []).map((k, i) => (
                  <span key={i} className="text-xs bg-amber-50 text-amber-700 px-2 py-1 rounded border border-amber-200 font-bold">{k}</span>
                ))}
              </div>
            </div>
          )}

          {/* Vocabulary */}
          {lp.vocabulary && (
            <div className="bg-white border border-gray-200 rounded-lg p-4">
              <h2 className="text-sm font-bold text-gray-700 mb-2">Vocabulary</h2>
              <div className="grid md:grid-cols-2 gap-3">
                {lp.vocabulary.tier2 && lp.vocabulary.tier2.length > 0 && (
                  <div>
                    <span className="text-[10px] font-bold text-orange-600 uppercase">Tier 2 (Academic)</span>
                    <div className="flex flex-wrap gap-1.5 mt-1">
                      {lp.vocabulary.tier2.map((w, i) => <span key={i} className="text-xs bg-orange-50 text-orange-700 px-2 py-0.5 rounded border border-orange-200">{w}</span>)}
                    </div>
                  </div>
                )}
                {lp.vocabulary.tier3 && lp.vocabulary.tier3.length > 0 && (
                  <div>
                    <span className="text-[10px] font-bold text-purple-600 uppercase">Tier 3 (Domain-Specific)</span>
                    <div className="flex flex-wrap gap-1.5 mt-1">
                      {lp.vocabulary.tier3.map((w, i) => <span key={i} className="text-xs bg-purple-50 text-purple-700 px-2 py-0.5 rounded border border-purple-200">{w}</span>)}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Materials */}
          {lp.materials && lp.materials.length > 0 && (
            <div className="bg-white border border-gray-200 rounded-lg p-4">
              <h2 className="text-sm font-bold text-gray-700 mb-2">Materials Needed</h2>
              <ul className="grid grid-cols-2 gap-1">
                {lp.materials.map((m, i) => <li key={i} className="text-xs text-gray-600 flex items-start gap-1"><CheckCircle size={12} className="text-green-500 mt-0.5 flex-shrink-0" /> {m}</li>)}
              </ul>
            </div>
          )}

          {/* Lesson Phases */}
          {lp.lesson_phases && (
            <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
              <div className="bg-gray-50 px-4 py-2.5 border-b border-gray-200">
                <h2 className="text-sm font-bold text-gray-700">Lesson Phases</h2>
              </div>
              <div className="divide-y divide-gray-100">
                {lp.lesson_phases.map((phase, i) => {
                  const isOpen = expandedPhase === i;
                  const phaseStyle = PHASE_ICONS[phase.phase] || "bg-gray-100 text-gray-700";
                  return (
                    <div key={i} className="cursor-pointer" onClick={() => setExpandedPhase(isOpen ? null : i)} data-testid={`lp-phase-${i}`}>
                      <div className="flex items-center justify-between px-4 py-3 hover:bg-gray-50">
                        <div className="flex items-center gap-3">
                          <span className={`w-8 h-8 flex items-center justify-center rounded-full text-xs font-bold ${phaseStyle}`}>{i + 1}</span>
                          <div>
                            <div className="text-sm font-semibold text-gray-800">{phase.phase}</div>
                            <div className="text-[10px] text-gray-400">{phase.duration}</div>
                          </div>
                        </div>
                        {isOpen ? <ChevronUp size={16} className="text-gray-400" /> : <ChevronDown size={16} className="text-gray-400" />}
                      </div>
                      {isOpen && (
                        <div className="px-4 pb-4 space-y-2 bg-gray-50/50">
                          <div className="text-xs"><span className="font-bold text-gray-600">Activities:</span> <span className="text-gray-700">{phase.activities}</span></div>
                          <div className="text-xs"><span className="font-bold text-blue-600">Teacher:</span> <span className="text-gray-700">{phase.teacher_actions}</span></div>
                          <div className="text-xs"><span className="font-bold text-green-600">Students:</span> <span className="text-gray-700">{phase.student_actions}</span></div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Sentence Frames */}
          {lp.sentence_frames && lp.sentence_frames.length > 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <div className="flex items-center gap-1.5 mb-2"><MessageSquare size={14} className="text-amber-700" /><h2 className="text-sm font-bold text-amber-800">Sentence Frames</h2></div>
              <div className="space-y-3">
                {lp.sentence_frames.map((sf, i) => (
                  <div key={i}>
                    <span className="text-[10px] font-bold text-amber-700 uppercase">{sf.level_range}</span>
                    <ul className="mt-1 space-y-0.5">
                      {(sf.frames || []).map((f, j) => <li key={j} className="text-xs text-amber-900 font-mono bg-white/60 px-2 py-1 rounded border border-amber-200">{f}</li>)}
                    </ul>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Scaffolding */}
          {lp.scaffolding && (
            <div className="bg-white border border-gray-200 rounded-lg p-4">
              <div className="flex items-center gap-1.5 mb-2"><Lightbulb size={14} className="text-gray-700" /><h2 className="text-sm font-bold text-gray-700">Scaffolding Strategies</h2></div>
              <div className="grid md:grid-cols-3 gap-3">
                {lp.scaffolding.sensory && (
                  <div><span className="text-[10px] font-bold text-teal-600 uppercase">Sensory</span>
                    <ul className="mt-1 space-y-0.5">{lp.scaffolding.sensory.map((s, i) => <li key={i} className="text-xs text-gray-600">- {s}</li>)}</ul>
                  </div>
                )}
                {lp.scaffolding.graphic && (
                  <div><span className="text-[10px] font-bold text-sky-600 uppercase">Graphic</span>
                    <ul className="mt-1 space-y-0.5">{lp.scaffolding.graphic.map((s, i) => <li key={i} className="text-xs text-gray-600">- {s}</li>)}</ul>
                  </div>
                )}
                {lp.scaffolding.interactive && (
                  <div><span className="text-[10px] font-bold text-violet-600 uppercase">Interactive</span>
                    <ul className="mt-1 space-y-0.5">{lp.scaffolding.interactive.map((s, i) => <li key={i} className="text-xs text-gray-600">- {s}</li>)}</ul>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Differentiation */}
          {lp.differentiation && (
            <div className="bg-white border border-gray-200 rounded-lg p-4">
              <h2 className="text-sm font-bold text-gray-700 mb-2">Differentiation</h2>
              <div className="space-y-2">
                {lp.differentiation.tier_a && <div className="text-xs"><span className="font-bold text-red-600">Tier A (L1-3):</span> <span className="text-gray-700">{lp.differentiation.tier_a}</span></div>}
                {lp.differentiation.tier_bc && <div className="text-xs"><span className="font-bold text-blue-600">Tier B/C (L3-6):</span> <span className="text-gray-700">{lp.differentiation.tier_bc}</span></div>}
                {lp.differentiation.extensions && <div className="text-xs"><span className="font-bold text-green-600">Extensions:</span> <span className="text-gray-700">{lp.differentiation.extensions}</span></div>}
              </div>
            </div>
          )}

          {/* Formative Check */}
          {lp.formative_check && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-center gap-1.5 mb-2"><CheckCircle size={14} className="text-green-700" /><h2 className="text-sm font-bold text-green-800">Formative Check / Exit Ticket</h2></div>
              <div className="text-xs text-green-900 mb-1"><span className="font-bold">Type:</span> {lp.formative_check.type}</div>
              <div className="text-xs text-green-900 mb-2"><span className="font-bold">Prompt:</span> {lp.formative_check.prompt}</div>
              {lp.formative_check.success_criteria && (
                <div>
                  <span className="text-[10px] font-bold text-green-700 uppercase">Success Criteria:</span>
                  <ul className="mt-1 space-y-0.5">{lp.formative_check.success_criteria.map((c, i) => <li key={i} className="text-xs text-green-800 flex items-start gap-1"><CheckCircle size={10} className="mt-0.5 flex-shrink-0" /> {c}</li>)}</ul>
                </div>
              )}
            </div>
          )}

          {/* Home Connection */}
          {lp.home_connection && (
            <div className="bg-white border border-gray-200 rounded-lg p-4">
              <div className="flex items-center gap-1.5 mb-1"><Home size={14} className="text-gray-700" /><h2 className="text-sm font-bold text-gray-700">Home Connection</h2></div>
              <p className="text-xs text-gray-600">{lp.home_connection}</p>
            </div>
          )}
        </div>
      </div>
    );
  }

  return null;
};

export default LessonPlanTab;
